import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { setUserTimeZone } from 'redux/slices';
import { useChangeUserTimezoneMutation } from 'redux/apiSlices/user.slices';
import { detectBrowserTimezone } from 'helpers/timezoneUtils';

/**
 * Auto-set browser timezone for new users.
 *
 * New users have user.timeZone = null. This hook detects the browser timezone
 * and sets it as the user's profile timezone automatically on first login.
 */
export const useAutoSetBrowserTimezone = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.authSlice.user);
  const [changeTimezone] = useChangeUserTimezoneMutation();
  const hasAttempted = useRef(false);

  useEffect(() => {
    const autoSetTimezone = async () => {
      // Only for new users (user.timeZone is null)
      if (!user?.id || user.timeZone !== null || hasAttempted.current) {
        return;
      }

      hasAttempted.current = true;

      const browserTimezone = detectBrowserTimezone();
      if (!browserTimezone) {
        return;
      }

      try {
        await changeTimezone({
          resourceId: user.id,
          timeZone: browserTimezone,
        }).unwrap();

        dispatch(setUserTimeZone(browserTimezone));
      } catch (error) {
        console.error('Failed to auto-set timezone:', error);
      }
    };

    autoSetTimezone();
  }, [user?.id, user?.timeZone, changeTimezone, dispatch]);
};

export default useAutoSetBrowserTimezone;
